import { NgClass } from '@angular/common'
import type { OnInit } from '@angular/core'
import { Component, Input, inject } from '@angular/core'
import {
  type AbstractControl,
  FormControl,
  type FormGroup,
  type ValidationErrors,
} from '@angular/forms'
import {
  ControlContainer,
  FormGroupDirective,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms'

import { TranslatePipe, TranslateService } from '@ngx-translate/core'

import type {
  EditableFieldDefinition,
  FieldDefinitionOption,
} from '../../types/field-types'
import type { SelectOption } from '../forms/select-input/select-input.component'
import { SelectInputComponent } from '../forms/select-input/select-input.component'

@Component({
  selector: 'dhp-input-field',
  templateUrl: './input-field.component.html',
  viewProviders: [
    { provide: ControlContainer, useExisting: FormGroupDirective },
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    SelectInputComponent,
    TranslatePipe,
  ],
})
export class InputFieldComponent implements OnInit {
  private formGroupDirective = inject(FormGroupDirective)
  private translate = inject(TranslateService)
  private readonly emptyControl = new FormControl<string | null>(null)
  parentFormGroup!: FormGroup
  // TODO: Skipped for migration because:
  //  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @Input({ required: true }) field!: EditableFieldDefinition

  ngOnInit(): void {
    this.parentFormGroup = this.formGroupDirective.form
  }

  isRequired(control: AbstractControl): boolean {
    if (this.field.showRequiredIndicator) {
      return true
    }

    const validator = control.validator
    if (!validator) {
      return false
    }

    return validator(this.emptyControl)?.['required'] === true
  }

  shouldShowErrors(control: AbstractControl): boolean {
    return (
      control.invalid && (control.touched || this.formGroupDirective.submitted)
    )
  }

  getControlErrors(
    control: AbstractControl
  ): { key: string; value: ValidationErrors[string] }[] {
    return Object.entries(control.errors ?? {}).map(([key, value]) => ({
      key,
      value,
    }))
  }

  toSelectOptions(options: FieldDefinitionOption[] = []): SelectOption[] {
    return options.map(opt => ({
      value: opt.value,
      label: String(this.translate.instant(opt.labelKey)),
      disabled: opt.disabled ?? false,
    }))
  }
}
